/**
 * Pure scheduling math for the fleet rotation grid.
 *
 * No React, no DOM, no worker globals: the UI uses this for instant feedback
 * and the simulation worker can import the same validators as the
 * authoritative check.
 *
 * Model notes
 *  - Flight legs recur daily. All times are simulation time (UTC).
 *  - A leg "occupies" its aircraft for blockMinutes + minTurnaroundMinutes.
 *  - Maintenance events are one-off windows on absolute sim minutes and take
 *    precedence over recurring legs.
 */

import type {
  FleetAircraftView,
  AircraftType,
  AirportView,
  FlightLeg,
  FlightSchedule,
  FlightStatusSnapshot,
  MaintenanceCheckType,
  MaintenanceEvent,
  NewFlightLeg,
  NewMaintenanceEvent,
  SimClock,
} from '../models/contracts';

/* ------------------------------------------------------------------ */
/* Constants                                                           */
/* ------------------------------------------------------------------ */

export const MINUTES_PER_DAY = 24 * 60;
export const SNAP_MINUTES = 5;
/** Taxi, climb and approach overhead added to cruise time to get block time. */
export const BLOCK_OVERHEAD_MINUTES = 30;
const EARTH_RADIUS_KM = 6371;

export interface CheckRule {
  readonly label: string;
  /** null = calendar-driven (D-check), no flight-hour interval. */
  readonly intervalHours: number | null;
  readonly downtimeMinutes: number;
  readonly requiresMaintenanceBase: boolean;
  /** Warn when this many flight hours (or fewer) remain... */
  readonly warnWindowHours: number;
  /** ...or when fewer than this many days of scheduled flying remain. */
  readonly warnLookaheadDays: number;
}

export const CHECK_RULES: Readonly<Record<MaintenanceCheckType, CheckRule>> = {
  A_CHECK: {
    label: 'A-Check',
    intervalHours: 500,
    downtimeMinutes: 12 * 60,
    requiresMaintenanceBase: false,
    warnWindowHours: 50,
    warnLookaheadDays: 7,
  },
  C_CHECK: {
    label: 'C-Check',
    intervalHours: 6000,
    downtimeMinutes: 7 * MINUTES_PER_DAY,
    requiresMaintenanceBase: true,
    warnWindowHours: 300,
    warnLookaheadDays: 30,
  },
  D_CHECK: {
    label: 'D-Check',
    intervalHours: null,
    downtimeMinutes: 21 * MINUTES_PER_DAY,
    requiresMaintenanceBase: true,
    warnWindowHours: 0,
    warnLookaheadDays: 0,
  },
};

/* ------------------------------------------------------------------ */
/* Time helpers                                                        */
/* ------------------------------------------------------------------ */

export function mod(value: number, modulus: number): number {
  return ((value % modulus) + modulus) % modulus;
}

export function pad2(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

export function clockToAbsMinute(clock: SimClock): number {
  return clock.dayIndex * MINUTES_PER_DAY + clock.minuteOfDay;
}

/** "HH:MM" for a minute-of-day (wraps, floors fractional minutes). */
export function formatMinuteOfDay(minute: number): string {
  const m = Math.floor(mod(minute, MINUTES_PER_DAY));
  return `${pad2(Math.floor(m / 60))}:${pad2(m % 60)}`;
}

export function formatDuration(minutes: number): string {
  const total = Math.max(0, Math.round(minutes));
  const h = Math.floor(total / 60);
  const m = total % 60;
  return h === 0 ? `${m}m` : `${h}h ${pad2(m)}m`;
}

/** "Day 12 08:30" for an absolute sim minute. */
export function formatSimTime(absMinute: number): string {
  const day = Math.floor(absMinute / MINUTES_PER_DAY);
  return `Day ${day} ${formatMinuteOfDay(absMinute)}`;
}

/** Parses an <input type="time"> value ("HH:MM") to a minute-of-day. */
export function parseTimeInput(value: string): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value);
  if (match === null) return null;
  const hh = match[1];
  const mm = match[2];
  if (hh === undefined || mm === undefined) return null;
  const h = Number(hh);
  const m = Number(mm);
  if (!Number.isInteger(h) || !Number.isInteger(m) || h > 23 || m > 59) return null;
  return h * 60 + m;
}

export function snapMinute(minute: number): number {
  const snapped = Math.round(minute / SNAP_MINUTES) * SNAP_MINUTES;
  return Math.min(MINUTES_PER_DAY - SNAP_MINUTES, Math.max(0, snapped));
}

export function ceilToSnap(minute: number): number {
  return Math.ceil(minute / SNAP_MINUTES) * SNAP_MINUTES;
}

/* ------------------------------------------------------------------ */
/* Circular (24 h wrap-around) interval helpers                        */
/* ------------------------------------------------------------------ */

/** True when two recurring [start, start+len) intervals overlap on a 24 h cycle. */
export function circularOverlap(
  startA: number,
  lenA: number,
  startB: number,
  lenB: number,
): boolean {
  for (const k of [-1, 0, 1]) {
    const offsetB = startB + k * MINUTES_PER_DAY;
    if (startA < offsetB + lenB && offsetB < startA + lenA) return true;
  }
  return false;
}

export function isWithinCircular(point: number, start: number, len: number): boolean {
  return mod(point - start, MINUTES_PER_DAY) < len;
}

export interface MinuteSegment {
  readonly start: number;
  readonly end: number;
}

/** Splits a recurring interval into 1-2 segments inside [0, 1440]. */
export function splitCircular(start: number, len: number): MinuteSegment[] {
  if (len <= 0) return [];
  if (len >= MINUTES_PER_DAY) return [{ start: 0, end: MINUTES_PER_DAY }];
  const s = mod(start, MINUTES_PER_DAY);
  const e = s + len;
  if (e <= MINUTES_PER_DAY) return [{ start: s, end: e }];
  return [
    { start: s, end: MINUTES_PER_DAY },
    { start: 0, end: e - MINUTES_PER_DAY },
  ];
}

/* ------------------------------------------------------------------ */
/* Geometry & block time                                               */
/* ------------------------------------------------------------------ */

export type GeoPoint = Pick<AirportView, 'lat' | 'lng'>;

export function haversineKm(a: GeoPoint, b: GeoPoint): number {
  const toRad = (deg: number): number => (deg * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Block time estimate, rounded up to the next 5 minutes. 0 if speed is invalid. */
export function estimateBlockMinutes(distanceKm: number, cruiseSpeedKmh: number): number {
  if (cruiseSpeedKmh <= 0) return 0;
  const raw = (distanceKm / cruiseSpeedKmh) * 60 + BLOCK_OVERHEAD_MINUTES;
  return Math.ceil(raw / SNAP_MINUTES) * SNAP_MINUTES;
}

/* ------------------------------------------------------------------ */
/* Selectors                                                           */
/* ------------------------------------------------------------------ */

export function groupByAircraft<T extends { readonly aircraftReg: string }>(
  items: readonly T[],
): Map<string, T[]> {
  const grouped = new Map<string, T[]>();
  for (const item of items) {
    const bucket = grouped.get(item.aircraftReg);
    if (bucket === undefined) grouped.set(item.aircraftReg, [item]);
    else bucket.push(item);
  }
  return grouped;
}

export function legsForAircraft(schedule: FlightSchedule, reg: string): FlightLeg[] {
  return schedule.legs.filter((leg) => leg.aircraftReg === reg);
}

export function arrivalMinute(leg: FlightLeg): number {
  return mod(leg.departureMinute + leg.blockMinutes, MINUTES_PER_DAY);
}

export function legLabel(leg: Pick<FlightLeg, 'origin' | 'destination' | 'departureMinute'>): string {
  return `${leg.origin}→${leg.destination} ${formatMinuteOfDay(leg.departureMinute)}`;
}

/**
 * Where the aircraft sits (on the ground) at a minute-of-day: the destination
 * of the leg that arrived most recently on the daily cycle, or its base if it
 * has no legs.
 */
export function locationAt(
  aircraft: FleetAircraftView,
  legs: readonly FlightLeg[],
  minuteOfDay: number,
): string {
  let best: FlightLeg | null = null;
  let bestSince = Number.POSITIVE_INFINITY;
  for (const leg of legs) {
    const since = mod(minuteOfDay - arrivalMinute(leg), MINUTES_PER_DAY);
    if (since < bestSince) {
      bestSince = since;
      best = leg;
    }
  }
  return best === null ? aircraft.baseAirportId : best.destination;
}

/* ------------------------------------------------------------------ */
/* Constraint validation                                               */
/* ------------------------------------------------------------------ */

export interface ScheduleContext {
  readonly aircraftTypes: Readonly<Record<string, AircraftType>>;
  readonly airports: Readonly<Record<string, AirportView>>;
  readonly fleet: readonly FleetAircraftView[];
  readonly schedule: FlightSchedule;
  readonly nowAbsMinute: number;
}

export type ConstraintSeverity = 'ERROR' | 'WARNING';

export type ConstraintCode =
  | 'UNKNOWN_AIRCRAFT'
  | 'UNKNOWN_AIRCRAFT_TYPE'
  | 'UNKNOWN_AIRPORT'
  | 'SAME_AIRPORT'
  | 'TIME_INVALID'
  | 'DURATION_INVALID'
  | 'RANGE_EXCEEDED'
  | 'RUNWAY_TOO_SHORT'
  | 'ORIGIN_MISMATCH'
  | 'SCHEDULE_OVERLAP'
  | 'TURNAROUND_TOO_SHORT'
  | 'SLOT_CAPACITY'
  | 'MAINTENANCE_OVERLAP'
  | 'MAINTENANCE_IN_PAST'
  | 'NO_MAINTENANCE_BASE'
  | 'CONTINUITY_BREAK'
  | 'CHECK_DUE';

export interface ConstraintIssue {
  readonly severity: ConstraintSeverity;
  readonly code: ConstraintCode;
  readonly message: string;
}

function issue(
  severity: ConstraintSeverity,
  code: ConstraintCode,
  message: string,
): ConstraintIssue {
  return { severity, code, message };
}

export function hasErrors(issues: readonly ConstraintIssue[]): boolean {
  return issues.some((i) => i.severity === 'ERROR');
}

function sortIssues(issues: ConstraintIssue[]): ConstraintIssue[] {
  return [...issues].sort((a, b) => {
    if (a.severity === b.severity) return 0;
    return a.severity === 'ERROR' ? -1 : 1;
  });
}

function findAircraft(
  fleet: readonly FleetAircraftView[],
  reg: string,
): FleetAircraftView | undefined {
  return fleet.find((a) => a.registration === reg);
}

/** Maintenance of one day or less blocks overlapping legs; longer checks suppress them. */
export function isBlockingMaintenance(durationMinutes: number): boolean {
  return durationMinutes <= MINUTES_PER_DAY;
}

/** True when a recurring leg's flight block overlaps the event on any day. */
function legOverlapsEvent(
  departureMinute: number,
  blockMinutes: number,
  event: Pick<MaintenanceEvent, 'startAbsMinute' | 'durationMinutes'>,
): boolean {
  const evStart = event.startAbsMinute;
  const evEnd = evStart + event.durationMinutes;
  const firstDay = Math.floor(evStart / MINUTES_PER_DAY) - 1;
  const lastDay = Math.floor(evEnd / MINUTES_PER_DAY);
  for (let day = firstDay; day <= lastDay; day += 1) {
    const s = day * MINUTES_PER_DAY + departureMinute;
    if (s < evEnd && evStart < s + blockMinutes) return true;
  }
  return false;
}

function countOpsAtHour(legs: readonly FlightLeg[], airportId: string, hour: number): number {
  let ops = 0;
  for (const leg of legs) {
    if (leg.origin === airportId && Math.floor(leg.departureMinute / 60) === hour) ops += 1;
    if (leg.destination === airportId && Math.floor(arrivalMinute(leg) / 60) === hour) ops += 1;
  }
  return ops;
}

export function validateNewLeg(ctx: ScheduleContext, draft: NewFlightLeg): ConstraintIssue[] {
  const aircraft = findAircraft(ctx.fleet, draft.aircraftReg);
  if (aircraft === undefined) {
    return [issue('ERROR', 'UNKNOWN_AIRCRAFT', `Aircraft ${draft.aircraftReg} is not in your fleet.`)];
  }
  const type = ctx.aircraftTypes[aircraft.typeId];
  if (type === undefined) {
    return [issue('ERROR', 'UNKNOWN_AIRCRAFT_TYPE', `Unknown aircraft type ${aircraft.typeId}.`)];
  }
  const origin = ctx.airports[draft.origin];
  const destination = ctx.airports[draft.destination];
  if (origin === undefined || destination === undefined) {
    return [issue('ERROR', 'UNKNOWN_AIRPORT', 'Origin or destination airport is not in the database.')];
  }
  if (draft.origin === draft.destination) {
    return [issue('ERROR', 'SAME_AIRPORT', 'Origin and destination must differ.')];
  }
  if (
    !Number.isInteger(draft.departureMinute) ||
    draft.departureMinute < 0 ||
    draft.departureMinute >= MINUTES_PER_DAY
  ) {
    return [issue('ERROR', 'TIME_INVALID', 'Departure time must be within a single day.')];
  }
  if (!Number.isInteger(draft.blockMinutes) || draft.blockMinutes <= 0) {
    return [issue('ERROR', 'DURATION_INVALID', 'Flight duration must be a positive whole number of minutes.')];
  }

  const issues: ConstraintIssue[] = [];
  const turnaround = type.minTurnaroundMinutes;
  const occupied = draft.blockMinutes + turnaround;
  if (occupied > MINUTES_PER_DAY) {
    issues.push(
      issue(
        'ERROR',
        'DURATION_INVALID',
        `Flight (${formatDuration(draft.blockMinutes)}) plus ${turnaround} min turnaround exceeds 24 h.`,
      ),
    );
    return issues;
  }

  // Range
  const distanceKm = haversineKm(origin, destination);
  if (distanceKm > type.maxRangeKm) {
    issues.push(
      issue(
        'ERROR',
        'RANGE_EXCEEDED',
        `${Math.round(distanceKm).toLocaleString()} km exceeds ${type.id} range of ${Math.round(type.maxRangeKm).toLocaleString()} km.`,
      ),
    );
  }

  // Runways
  for (const airport of [origin, destination]) {
    if (airport.runwayLengthMeters < type.minRunwayRequiredMeters) {
      issues.push(
        issue(
          'ERROR',
          'RUNWAY_TOO_SHORT',
          `${airport.id} runway (${Math.round(airport.runwayLengthMeters).toLocaleString()} m) is shorter than the ${Math.round(type.minRunwayRequiredMeters).toLocaleString()} m ${type.id} needs.`,
        ),
      );
    }
  }

  // Aircraft position continuity
  const legs = legsForAircraft(ctx.schedule, aircraft.registration);
  const expectedOrigin = locationAt(aircraft, legs, draft.departureMinute);
  if (expectedOrigin !== draft.origin) {
    issues.push(
      issue(
        'ERROR',
        'ORIGIN_MISMATCH',
        `${aircraft.registration} will be at ${expectedOrigin} at ${formatMinuteOfDay(draft.departureMinute)}, not ${draft.origin}.`,
      ),
    );
  }

  // Overlap & turnaround against the aircraft's other legs
  for (const other of legs) {
    const otherOccupied = other.blockMinutes + turnaround;
    if (!circularOverlap(draft.departureMinute, occupied, other.departureMinute, otherOccupied)) {
      continue;
    }
    const flightsOverlap = circularOverlap(
      draft.departureMinute,
      draft.blockMinutes,
      other.departureMinute,
      other.blockMinutes,
    );
    if (flightsOverlap) {
      issues.push(
        issue('ERROR', 'SCHEDULE_OVERLAP', `Overlaps ${legLabel(other)} on the same aircraft.`),
      );
    } else {
      issues.push(
        issue(
          'ERROR',
          'TURNAROUND_TOO_SHORT',
          `Needs a ${turnaround} min turnaround next to ${legLabel(other)}.`,
        ),
      );
    }
  }

  // Airport slot capacity (fleet-wide, per sim hour)
  const arrival = mod(draft.departureMinute + draft.blockMinutes, MINUTES_PER_DAY);
  const slotChecks: ReadonlyArray<{ airport: AirportView; minute: number; verb: string }> = [
    { airport: origin, minute: draft.departureMinute, verb: 'departure' },
    { airport: destination, minute: arrival, verb: 'arrival' },
  ];
  for (const { airport, minute, verb } of slotChecks) {
    const hour = Math.floor(minute / 60);
    const capacity = airport.slotCapacityHourly[hour];
    if (capacity === undefined) continue;
    const ops = countOpsAtHour(ctx.schedule.legs, airport.id, hour) + 1;
    if (ops > capacity) {
      issues.push(
        issue(
          'ERROR',
          'SLOT_CAPACITY',
          `${airport.id} has no free slot for a ${verb} at ${pad2(hour)}:00 (${capacity} ops/hr limit).`,
        ),
      );
    }
  }

  // Scheduled maintenance
  for (const event of ctx.schedule.maintenance) {
    if (event.aircraftReg !== aircraft.registration) continue;
    if (event.startAbsMinute + event.durationMinutes <= ctx.nowAbsMinute) continue;
    if (!legOverlapsEvent(draft.departureMinute, draft.blockMinutes, event)) continue;
    const label = CHECK_RULES[event.check].label;
    if (isBlockingMaintenance(event.durationMinutes)) {
      issues.push(
        issue(
          'ERROR',
          'MAINTENANCE_OVERLAP',
          `Collides with the ${label} starting ${formatSimTime(event.startAbsMinute)}.`,
        ),
      );
    } else {
      issues.push(
        issue(
          'WARNING',
          'MAINTENANCE_OVERLAP',
          `This leg will be suppressed during the ${label} starting ${formatSimTime(event.startAbsMinute)}.`,
        ),
      );
    }
  }

  // Next leg must depart from where this one lands
  let nextLeg: FlightLeg | null = null;
  let nextGap = Number.POSITIVE_INFINITY;
  for (const other of legs) {
    const gap = mod(other.departureMinute - arrival, MINUTES_PER_DAY);
    if (gap < nextGap) {
      nextGap = gap;
      nextLeg = other;
    }
  }
  if (nextLeg !== null && nextLeg.origin !== draft.destination) {
    issues.push(
      issue(
        'WARNING',
        'CONTINUITY_BREAK',
        `Next leg ${legLabel(nextLeg)} departs ${nextLeg.origin}, but this leg lands at ${draft.destination}.`,
      ),
    );
  }

  // Check-due projection with this leg included
  const projected: FlightLeg = { id: '__draft__', ...draft };
  const statuses = computeCheckStatuses(
    aircraft,
    [...legs, projected],
    ctx.schedule.maintenance.filter((e) => e.aircraftReg === aircraft.registration),
    ctx.nowAbsMinute,
  );
  for (const status of statuses) {
    if (status.severity === 'APPROACHING' || status.severity === 'OVERDUE') {
      issues.push(issue('WARNING', 'CHECK_DUE', checkStatusMessage(status)));
    }
  }

  return sortIssues(issues);
}

export function validateNewMaintenance(
  ctx: ScheduleContext,
  draft: NewMaintenanceEvent,
): ConstraintIssue[] {
  const aircraft = findAircraft(ctx.fleet, draft.aircraftReg);
  if (aircraft === undefined) {
    return [issue('ERROR', 'UNKNOWN_AIRCRAFT', `Aircraft ${draft.aircraftReg} is not in your fleet.`)];
  }
  const airport = ctx.airports[draft.airportId];
  if (airport === undefined) {
    return [issue('ERROR', 'UNKNOWN_AIRPORT', `Airport ${draft.airportId} is not in the database.`)];
  }
  if (!Number.isFinite(draft.startAbsMinute) || draft.durationMinutes <= 0) {
    return [issue('ERROR', 'DURATION_INVALID', 'Maintenance needs a valid start and a positive duration.')];
  }

  const issues: ConstraintIssue[] = [];
  const rule = CHECK_RULES[draft.check];
  const endAbs = draft.startAbsMinute + draft.durationMinutes;

  if (draft.startAbsMinute < ctx.nowAbsMinute) {
    issues.push(issue('ERROR', 'MAINTENANCE_IN_PAST', 'Maintenance cannot start in the past.'));
  }

  if (rule.requiresMaintenanceBase && !airport.hasMaintenanceBase) {
    issues.push(
      issue(
        'ERROR',
        'NO_MAINTENANCE_BASE',
        `${rule.label} needs a heavy maintenance base; ${airport.id} has none.`,
      ),
    );
  }

  const legs = legsForAircraft(ctx.schedule, aircraft.registration);
  const expected = locationAt(aircraft, legs, mod(draft.startAbsMinute, MINUTES_PER_DAY));
  if (expected !== draft.airportId) {
    issues.push(
      issue(
        'ERROR',
        'ORIGIN_MISMATCH',
        `${aircraft.registration} will be at ${expected} at ${formatMinuteOfDay(draft.startAbsMinute)}, not ${draft.airportId}.`,
      ),
    );
  }

  for (const other of ctx.schedule.maintenance) {
    if (other.aircraftReg !== aircraft.registration) continue;
    const otherEnd = other.startAbsMinute + other.durationMinutes;
    if (otherEnd <= ctx.nowAbsMinute) continue;
    if (draft.startAbsMinute < otherEnd && other.startAbsMinute < endAbs) {
      issues.push(
        issue(
          'ERROR',
          'MAINTENANCE_OVERLAP',
          `Overlaps the ${CHECK_RULES[other.check].label} starting ${formatSimTime(other.startAbsMinute)}.`,
        ),
      );
    }
  }

  const overlapping = legs.filter((leg) =>
    legOverlapsEvent(leg.departureMinute, leg.blockMinutes, draft),
  );
  if (overlapping.length > 0) {
    const names = overlapping.slice(0, 3).map(legLabel).join(', ');
    const extra = overlapping.length > 3 ? ` and ${overlapping.length - 3} more` : '';
    if (isBlockingMaintenance(draft.durationMinutes)) {
      issues.push(
        issue(
          'ERROR',
          'MAINTENANCE_OVERLAP',
          `Window is not clear of flights: ${names}${extra}. Delete them or pick another time.`,
        ),
      );
    } else {
      issues.push(
        issue(
          'WARNING',
          'MAINTENANCE_OVERLAP',
          `${overlapping.length} daily leg(s) will be suppressed while the aircraft is grounded: ${names}${extra}.`,
        ),
      );
    }
  }

  return sortIssues(issues);
}

/* ------------------------------------------------------------------ */
/* Maintenance status (A-check / C-check approach warnings)            */
/* ------------------------------------------------------------------ */

export type CheckSeverity = 'OK' | 'SCHEDULED' | 'APPROACHING' | 'OVERDUE';

export interface CheckStatus {
  readonly check: 'A_CHECK' | 'C_CHECK';
  readonly hoursSinceCheck: number;
  readonly hoursRemaining: number;
  readonly intervalHours: number;
  readonly dailyFlightHours: number;
  /** Absolute sim minute the interval is projected to run out, or null if not flying. */
  readonly projectedDueAbsMinute: number | null;
  /** Start of the earliest unfinished scheduled check of this type, if any. */
  readonly scheduledStartAbsMinute: number | null;
  readonly severity: CheckSeverity;
}

export function dailyFlightHours(legs: readonly FlightLeg[]): number {
  let minutes = 0;
  for (const leg of legs) minutes += leg.blockMinutes;
  return minutes / 60;
}

export function computeCheckStatuses(
  aircraft: FleetAircraftView,
  legs: readonly FlightLeg[],
  events: readonly MaintenanceEvent[],
  nowAbsMinute: number,
): CheckStatus[] {
  const daily = dailyFlightHours(legs);
  const statuses: CheckStatus[] = [];
  const checks: ReadonlyArray<'A_CHECK' | 'C_CHECK'> = ['A_CHECK', 'C_CHECK'];

  for (const check of checks) {
    const rule = CHECK_RULES[check];
    const interval = rule.intervalHours;
    if (interval === null) continue;

    const hoursSince =
      check === 'A_CHECK' ? aircraft.flightHoursSinceACheck : aircraft.flightHoursSinceCCheck;
    const remaining = interval - hoursSince;

    let projectedDue: number | null = null;
    if (remaining <= 0) projectedDue = nowAbsMinute;
    else if (daily > 0) projectedDue = nowAbsMinute + (remaining / daily) * MINUTES_PER_DAY;

    let scheduledStart: number | null = null;
    for (const event of events) {
      if (event.check !== check) continue;
      if (event.startAbsMinute + event.durationMinutes <= nowAbsMinute) continue;
      if (scheduledStart === null || event.startAbsMinute < scheduledStart) {
        scheduledStart = event.startAbsMinute;
      }
    }

    const covered =
      scheduledStart !== null && (projectedDue === null || scheduledStart <= projectedDue);
    const near = remaining <= Math.max(rule.warnWindowHours, daily * rule.warnLookaheadDays);

    let severity: CheckSeverity;
    if (remaining <= 0) {
      severity = scheduledStart !== null && scheduledStart <= nowAbsMinute ? 'SCHEDULED' : 'OVERDUE';
    } else if (!near) {
      severity = 'OK';
    } else {
      severity = covered ? 'SCHEDULED' : 'APPROACHING';
    }

    statuses.push({
      check,
      hoursSinceCheck: hoursSince,
      hoursRemaining: remaining,
      intervalHours: interval,
      dailyFlightHours: daily,
      projectedDueAbsMinute: projectedDue,
      scheduledStartAbsMinute: scheduledStart,
      severity,
    });
  }
  return statuses;
}

export function checkStatusMessage(status: CheckStatus): string {
  const label = CHECK_RULES[status.check].label;
  if (status.hoursRemaining <= 0) {
    return `${label} is overdue by ${Math.abs(status.hoursRemaining).toFixed(0)} flight hours. Assign ground time now.`;
  }
  const when =
    status.projectedDueAbsMinute === null
      ? ''
      : ` (projected ${formatSimTime(status.projectedDueAbsMinute)})`;
  return `${label} due in ${status.hoursRemaining.toFixed(0)} flight hours${when} with no ground time assigned before then.`;
}

/* ------------------------------------------------------------------ */
/* Live status                                                         */
/* ------------------------------------------------------------------ */

export type OpsStatus = 'IN_FLIGHT' | 'TURNAROUND' | 'IDLE' | 'MAINTENANCE';
export type FleetStatusCounts = Readonly<Record<OpsStatus, number>>;

export function opsStatusAt(
  legs: readonly FlightLeg[],
  events: readonly MaintenanceEvent[],
  turnaroundMinutes: number,
  absMinute: number,
): OpsStatus {
  for (const event of events) {
    if (absMinute >= event.startAbsMinute && absMinute < event.startAbsMinute + event.durationMinutes) {
      return 'MAINTENANCE';
    }
  }
  const minute = mod(absMinute, MINUTES_PER_DAY);
  let turning = false;
  for (const leg of legs) {
    if (isWithinCircular(minute, leg.departureMinute, leg.blockMinutes)) return 'IN_FLIGHT';
    if (isWithinCircular(minute, arrivalMinute(leg), turnaroundMinutes)) turning = true;
  }
  return turning ? 'TURNAROUND' : 'IDLE';
}

/**
 * Status breakdown for the whole fleet. The schedule drives the result; the
 * worker's hourly snapshot can only upgrade an aircraft to IN_FLIGHT.
 */
export function computeFleetStatusCounts(
  fleet: readonly FleetAircraftView[],
  aircraftTypes: Readonly<Record<string, AircraftType>>,
  schedule: FlightSchedule,
  absMinute: number,
  activeFlights: readonly FlightStatusSnapshot[],
): FleetStatusCounts {
  const legsByReg = groupByAircraft(schedule.legs);
  const eventsByReg = groupByAircraft(schedule.maintenance);
  const airborne = new Set<string>(activeFlights.map((f) => f.aircraftReg));
  const counts: Record<OpsStatus, number> = {
    IN_FLIGHT: 0,
    TURNAROUND: 0,
    IDLE: 0,
    MAINTENANCE: 0,
  };
  for (const aircraft of fleet) {
    const turnaround = aircraftTypes[aircraft.typeId]?.minTurnaroundMinutes ?? 0;
    const status = opsStatusAt(
      legsByReg.get(aircraft.registration) ?? [],
      eventsByReg.get(aircraft.registration) ?? [],
      turnaround,
      absMinute,
    );
    const effective: OpsStatus =
      status !== 'MAINTENANCE' && airborne.has(aircraft.registration) ? 'IN_FLIGHT' : status;
    counts[effective] += 1;
  }
  return counts;
}

/* ------------------------------------------------------------------ */
/* Utilization                                                         */
/* ------------------------------------------------------------------ */

export interface FleetUtilization {
  readonly aircraftCount: number;
  readonly flyingHours: number;
  readonly groundHours: number;
  readonly maintenanceHours: number;
  /** flying hours / (aircraft x 24 h) x 100 */
  readonly utilizationPct: number;
}

function maintenanceMinutesOnDay(
  events: readonly MaintenanceEvent[],
  dayIndex: number,
): number {
  const dayStart = dayIndex * MINUTES_PER_DAY;
  const dayEnd = dayStart + MINUTES_PER_DAY;
  let minutes = 0;
  for (const event of events) {
    const s = Math.max(event.startAbsMinute, dayStart);
    const e = Math.min(event.startAbsMinute + event.durationMinutes, dayEnd);
    if (e > s) minutes += e - s;
  }
  return minutes;
}

export function computeFleetUtilization(
  fleet: readonly FleetAircraftView[],
  schedule: FlightSchedule,
  dayIndex: number,
): FleetUtilization {
  const legsByReg = groupByAircraft(schedule.legs);
  const eventsByReg = groupByAircraft(schedule.maintenance);
  let flying = 0;
  let ground = 0;
  let maintenance = 0;
  for (const aircraft of fleet) {
    const legs = legsByReg.get(aircraft.registration) ?? [];
    const flyMin = Math.min(
      MINUTES_PER_DAY,
      legs.reduce((sum, leg) => sum + leg.blockMinutes, 0),
    );
    const mntMin = Math.min(
      MINUTES_PER_DAY,
      maintenanceMinutesOnDay(eventsByReg.get(aircraft.registration) ?? [], dayIndex),
    );
    flying += flyMin;
    maintenance += mntMin;
    ground += Math.max(0, MINUTES_PER_DAY - flyMin - mntMin);
  }
  const count = fleet.length;
  return {
    aircraftCount: count,
    flyingHours: flying / 60,
    groundHours: ground / 60,
    maintenanceHours: maintenance / 60,
    utilizationPct: count === 0 ? 0 : (flying / (count * MINUTES_PER_DAY)) * 100,
  };
}

/* ------------------------------------------------------------------ */
/* Timeline blocks                                                     */
/* ------------------------------------------------------------------ */

export type BlockKind = 'FLIGHT' | 'TURNAROUND' | 'MAINTENANCE' | 'CONFLICT';

export interface TimelineBlock {
  readonly key: string;
  readonly kind: BlockKind;
  readonly aircraftReg: string;
  /** Minutes from 00:00 of the viewed day, within [0, 1440]. */
  readonly startMinute: number;
  readonly endMinute: number;
  readonly label: string;
  readonly detail: string;
  readonly legId: string | null;
  readonly maintenanceId: string | null;
  readonly continuesFromPrevious: boolean;
  readonly continuesToNext: boolean;
}

/**
 * Legs of one aircraft that conflict with each other (overlapping flights or
 * insufficient turnaround) or with maintenance on the viewed day.
 */
export function findConflictingLegIds(
  legs: readonly FlightLeg[],
  turnaroundMinutes: number,
  events: readonly MaintenanceEvent[],
  dayIndex: number,
): Set<string> {
  const conflicts = new Set<string>();
  for (let i = 0; i < legs.length; i += 1) {
    const a = legs[i];
    if (a === undefined) continue;
    const occA = a.blockMinutes + turnaroundMinutes;
    if (occA > MINUTES_PER_DAY) conflicts.add(a.id);
    for (let j = i + 1; j < legs.length; j += 1) {
      const b = legs[j];
      if (b === undefined) continue;
      if (
        circularOverlap(a.departureMinute, occA, b.departureMinute, b.blockMinutes + turnaroundMinutes)
      ) {
        conflicts.add(a.id);
        conflicts.add(b.id);
      }
    }
  }

  const dayStart = dayIndex * MINUTES_PER_DAY;
  const dayEnd = dayStart + MINUTES_PER_DAY;
  for (const leg of legs) {
    for (const event of events) {
      const evStart = event.startAbsMinute;
      const evEnd = evStart + event.durationMinutes;
      for (const day of [dayIndex - 1, dayIndex]) {
        const s = day * MINUTES_PER_DAY + leg.departureMinute;
        const e = s + leg.blockMinutes;
        const visible = e > dayStart && s < dayEnd;
        if (visible && s < evEnd && evStart < e) conflicts.add(leg.id);
      }
    }
  }
  return conflicts;
}

export function buildTimelineBlocks(
  aircraftReg: string,
  legs: readonly FlightLeg[],
  events: readonly MaintenanceEvent[],
  turnaroundMinutes: number,
  dayIndex: number,
): TimelineBlock[] {
  const conflicts = findConflictingLegIds(legs, turnaroundMinutes, events, dayIndex);
  const turnaroundBlocks: TimelineBlock[] = [];
  const maintenanceBlocks: TimelineBlock[] = [];
  const flightBlocks: TimelineBlock[] = [];

  const sorted = [...legs].sort((a, b) => a.departureMinute - b.departureMinute);
  for (const leg of sorted) {
    const conflicting = conflicts.has(leg.id);
    const arrival = arrivalMinute(leg);

    const flightSegments = splitCircular(leg.departureMinute, leg.blockMinutes);
    flightSegments.forEach((segment, index) => {
      flightBlocks.push({
        key: `${leg.id}:f${index}`,
        kind: conflicting ? 'CONFLICT' : 'FLIGHT',
        aircraftReg,
        startMinute: segment.start,
        endMinute: segment.end,
        label: `${leg.origin}→${leg.destination}`,
        detail: `${formatMinuteOfDay(leg.departureMinute)}–${formatMinuteOfDay(arrival)} · ${formatDuration(leg.blockMinutes)}${conflicting ? ' · CONFLICT' : ''}`,
        legId: leg.id,
        maintenanceId: null,
        continuesFromPrevious: index > 0,
        continuesToNext: index < flightSegments.length - 1,
      });
    });

    const turnSegments = splitCircular(arrival, turnaroundMinutes);
    turnSegments.forEach((segment, index) => {
      turnaroundBlocks.push({
        key: `${leg.id}:t${index}`,
        kind: conflicting ? 'CONFLICT' : 'TURNAROUND',
        aircraftReg,
        startMinute: segment.start,
        endMinute: segment.end,
        label: 'TAT',
        detail: `Turnaround at ${leg.destination} · ${turnaroundMinutes} min minimum`,
        legId: leg.id,
        maintenanceId: null,
        continuesFromPrevious: index > 0,
        continuesToNext: index < turnSegments.length - 1,
      });
    });
  }

  const dayStart = dayIndex * MINUTES_PER_DAY;
  const dayEnd = dayStart + MINUTES_PER_DAY;
  for (const event of events) {
    const evEnd = event.startAbsMinute + event.durationMinutes;
    const s = Math.max(event.startAbsMinute, dayStart);
    const e = Math.min(evEnd, dayEnd);
    if (e <= s) continue;
    const label = CHECK_RULES[event.check].label;
    maintenanceBlocks.push({
      key: `${event.id}:m`,
      kind: 'MAINTENANCE',
      aircraftReg,
      startMinute: s - dayStart,
      endMinute: e - dayStart,
      label: label.toUpperCase(),
      detail: `${label} at ${event.airportId} · ${formatSimTime(event.startAbsMinute)} for ${formatDuration(event.durationMinutes)}`,
      legId: null,
      maintenanceId: event.id,
      continuesFromPrevious: event.startAbsMinute < dayStart,
      continuesToNext: evEnd > dayEnd,
    });
  }

  // DOM order is z-order: ground time at the back, flights on top.
  return [...turnaroundBlocks, ...maintenanceBlocks, ...flightBlocks];
}

/* ------------------------------------------------------------------ */
/* Destination candidates for the Add Flight Leg modal                 */
/* ------------------------------------------------------------------ */

export interface DestinationCandidate {
  readonly airport: AirportView;
  readonly distanceKm: number;
  readonly blockMinutes: number;
}

/** Airports reachable from `origin` by `type` (range + runway), nearest first. */
export function listDestinationCandidates(
  origin: AirportView,
  type: AircraftType,
  airports: Readonly<Record<string, AirportView>>,
  query: string,
  limit: number,
): DestinationCandidate[] {
  const needle = query.trim().toLowerCase();
  const candidates: DestinationCandidate[] = [];
  for (const airport of Object.values(airports)) {
    if (airport.id === origin.id) continue;
    if (airport.runwayLengthMeters < type.minRunwayRequiredMeters) continue;
    if (
      needle !== '' &&
      !airport.id.toLowerCase().includes(needle) &&
      !airport.name.toLowerCase().includes(needle)
    ) {
      continue;
    }
    const distanceKm = haversineKm(origin, airport);
    if (distanceKm > type.maxRangeKm) continue;
    candidates.push({
      airport,
      distanceKm,
      blockMinutes: estimateBlockMinutes(distanceKm, type.cruiseSpeedKmh),
    });
  }
  candidates.sort((a, b) => a.distanceKm - b.distanceKm);
  return candidates.slice(0, limit);
}
